import * as React from "react";
import styled, { css } from "styled-components";

import translate from "@insite/client-framework/Translate";
import { AttributeTypeFacetModel, FacetModel } from "@insite/client-framework/Types/ApiModels";
import ProductListFilterAccordionSection from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import Drawer from "@insite/mobius/Drawer";
import Filter from "@insite/mobius/Icons/Filter";
import { Clickable, Icon, Typography } from "@insite/mobius/index";

import { ItemWithHelpInfoHelp } from "@thkStore/Components/Tooltip/ItemWithHelpInfo";
import { CustomAttributeTypeFacetModel } from "@thkStore/Types/VariantChildrenGridModel";
import { ProductDetailsFilterList } from "@thkStore/Widgets/ProductDetails/ProductDetailsFilterList";
import { ProductDetailsSearchWithinVariants } from "@thkStore/Widgets/ProductDetails/ProductDetailsSearchWithinVariants";

interface VariantFilterDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onToggle: () => void;
    attributeFacets: AttributeTypeFacetModel[];
    attributeFacetsWithCustomProperties: CustomAttributeTypeFacetModel[];
    searchWithinQueries: string[];
    onClearAllFilters: () => void;
    onRemoveFilter: (attrTypeId: string, value: string) => void;
    onRemoveSearchTerm: (searchTerm: string) => void;
    onAddSearchFilters: (filterObj: { searchWithinQueries: string[] }) => void;
    onFilterChange: (attributeTypeId: string, facet: FacetModel) => void;
}

/** Styled filter button with theme colors */
const FilterButton = styled(Clickable)`
    background-color: ${({ theme }) => theme.colors.primary.main};
    color: ${({ theme }) => theme.colors.primary.contrast};
    border: ${({ theme }) => theme.colors.primary.main} solid 1px;
    border-radius: 100px;
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 4px 20px;
    
    &:hover {
        background-color: ${({ theme }) => theme.colors.primary.contrast};
        color: ${({ theme }) => theme.colors.primary.main};

        span {

            &[class^="IconWrapper-sc-"] {
                color: ${({ theme }) => theme.colors.primary.main};
            }
        }
    }

    span {

        &[class^="IconWrapper-sc-"] {
            color: ${({ theme }) => theme.colors.primary.contrast};
            width: 21px;
            height: 21px;
        }
    }
`;

const drawerStyles = {
    position: "left" as const,
    size: 350,
    closeButtonProps: {
        shape: "pill" as const,
        buttonType: "solid" as const,
        color: "common.background",
        size: 36,
    },
    cssOverrides: {
        drawerTitle: css`
            background: ${({ theme }) => theme.colors.common.background};
            position: absolute;
            align-self: flex-end;
        `,
        drawerBody: css`
            background: ${({ theme }) => theme.colors.common.background};
        `,
        drawerContent: css`
            padding: 32px;
        `,
    },
};

export const FilterContainer = styled.div`

    & > h3 {

        &[class^="TypographyStyle-sc-"] {
            padding: 0;
            background-color: transparent;
        }
    }

    [data-test-selector="productListFilterList"] {
        display: none;
    }

    [for="productsKeywordSearch"] + div {
        margin-bottom: 20px;
    }
`;

export const VariantFilterDrawer: React.FC<VariantFilterDrawerProps> = ({
    isOpen,
    onClose,
    onToggle,
    attributeFacets,
    attributeFacetsWithCustomProperties,
    searchWithinQueries,
    onClearAllFilters,
    onRemoveFilter,
    onRemoveSearchTerm,
    onAddSearchFilters,
    onFilterChange,
}) => {

    return (
        <>
            <FilterButton onClick={onToggle}>
                <Typography>Filters</Typography>
                <Icon src={Filter} />                
            </FilterButton>
            <Drawer {...drawerStyles} isOpen={isOpen} handleClose={onClose}>

                <FilterContainer>
                    
                    <Typography variant="h3">
                        {translate("Filter Variants")}
                    </Typography>

                    <ProductDetailsFilterList
                        attributeTypeFacets={attributeFacets}
                        clearAllProductFilters={onClearAllFilters}
                        removeAttribute={onRemoveFilter}
                        clickRemoveSearchHandler={onRemoveSearchTerm}
                        searchWithinQueries={searchWithinQueries}
                    />

                    <ProductDetailsSearchWithinVariants addProductFilters={onAddSearchFilters} />

                    {attributeFacetsWithCustomProperties.map(a => {
                        if (!a.attributeValueFacets || a.attributeValueFacets.length <= 0) {
                            return null;
                        }

                        const titleElement = (
                            <ItemWithHelpInfoHelp
                                title={a.nameDisplay}
                                helpContent={a.customProperties}
                            />
                        );

                        return (
                            <ProductListFilterAccordionSection
                                key={a.attributeTypeId}
                                title={titleElement as unknown as string}
                                facets={a.attributeValueFacets.map<FacetModel>(av => ({
                                    ...av,
                                    id: av.attributeValueId,
                                    name: av.valueDisplay,
                                }))}
                                onChangeFacet={(facet: FacetModel) => {
                                    facet.selected = !facet.selected;
                                    onFilterChange(a.attributeTypeId, facet);
                                }}
                                showMoreLimit={5}
                                expandByDefault={false}
                            />
                        );
                    })}

                </FilterContainer>

            </Drawer>
        </>
    );
};
